import React, { useState, useRef, useEffect, useCallback } from 'react';
import { HiChevronDown, HiX } from 'react-icons/hi';
import { Play, Pause, SkipBack, SkipForward } from 'lucide-react';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { useSettings } from '../../contexts/SettingsContext';
import useGaplessQuranAudio from '../Quran/hooks/useGaplessQuranAudio';
import useQuranData from '../Quran/hooks/useQuranData';

const SimpleQuranPlayerCard = ({ 
  accentColor,
  backgroundImages,
  currentBackgroundIndex,
  previousBackgroundIndex,
  onPlayerStateChange // Callback to notify parent about player state
}) => {
  const { isDarkMode } = useDarkMode();
  const { settings } = useSettings();
  const [isPlayerExpanded, setIsPlayerExpanded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const contentRef = useRef(null);
  const containerRef = useRef(null);
  const userScrollTimeoutRef = useRef(null);

  // Quran data and audio hooks
  const quranData = useQuranData();
  const selectedSurah = quranData.selectedSurah || '1';
  const selectedReciter = quranData.selectedReciter || '7';
  const currentVerse = quranData.currentVerse || 0;
  
  // Compute surah and reciter names for audio
  const surahName = quranData.surahs && selectedSurah
    ? quranData.surahs.find(s => s.id.toString() === selectedSurah)?.name_simple
    : undefined;
  const reciterName = quranData.reciters && selectedReciter
    ? quranData.reciters.find(r => r.id.toString() === selectedReciter)?.reciter_name
    : undefined;

  // Audio hook
  const quranAudio = useGaplessQuranAudio(
    selectedSurah,
    selectedReciter,
    currentVerse,
    quranData.setCurrentVerse,
    surahName,
    reciterName,
    quranData.markVerseAsRead,
    null // handleNativeStateSync
  );

  // Notify parent when player state changes
  useEffect(() => {
    if (onPlayerStateChange) {
      onPlayerStateChange(isPlayerExpanded);
    }
  }, [isPlayerExpanded, onPlayerStateChange]);

  // Handle play button click
  const handlePlayClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    quranAudio.togglePlay();
  };

  // Handle navigation clicks
  const handlePrevVerse = useCallback(() => {
    if (currentVerse > 0) {
      quranData.setCurrentVerse(currentVerse - 1);
    }
  }, [currentVerse, quranData.setCurrentVerse]);

  const handleNextVerse = useCallback(() => {
    if (quranData.surahContent && currentVerse < quranData.surahContent.length - 1) {
      quranData.setCurrentVerse(currentVerse + 1);
    }
  }, [currentVerse, quranData.surahContent, quranData.setCurrentVerse]);

  // Reset scroll position when verse changes or player expands
  useEffect(() => {
    setScrollPosition(0);
    setIsUserScrolling(false);
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [currentVerse, isPlayerExpanded]);

  // Handle scroll based on audio progress
  useEffect(() => {
    if (quranAudio.isPlaying && isPlayerExpanded && contentRef.current && !isUserScrolling) {
      const content = contentRef.current;
      const totalScrollHeight = content.scrollHeight - content.clientHeight;
      
      // Calculate scroll position based on audio progress
      const newScrollPosition = (quranAudio.audioProgress / 100) * totalScrollHeight;
      
      // Use requestAnimationFrame for smoother scrolling
      const smoothScroll = () => {
        const currentScroll = content.scrollTop;
        const diff = newScrollPosition - currentScroll;
        
        // If we're close enough, just set to final position
        if (Math.abs(diff) < 1) {
          content.scrollTop = newScrollPosition;
          return;
        }
        
        // Smooth step towards target
        const step = diff * 0.1;
        content.scrollTop = currentScroll + step;
        
        if (quranAudio.isPlaying && !isUserScrolling) {
          requestAnimationFrame(smoothScroll);
        }
      };
      
      requestAnimationFrame(smoothScroll);
    }
  }, [quranAudio.isPlaying, isPlayerExpanded, quranAudio.audioProgress, isUserScrolling]);

  // Handle user scroll interaction
  const handleScroll = () => {
    if (!isUserScrolling) {
      setIsUserScrolling(true);
    }

    // Clear existing timeout
    if (userScrollTimeoutRef.current) {
      clearTimeout(userScrollTimeoutRef.current);
    }

    // Reset user scrolling flag after 2 seconds of no scroll activity
    userScrollTimeoutRef.current = setTimeout(() => {
      setIsUserScrolling(false);
    }, 2000);
  };

  // Clean up timeouts
  useEffect(() => {
    return () => {
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setIsPlayerExpanded(false);
    }, 300);
  };

  const handleSideTabClick = () => {
    setIsPlayerExpanded(true);
  };

  const handleSurahSelect = (surahId) => {
    // Set loading state before changing surah
    setIsLoading(true);
    // Load the selected surah content
    quranData.loadSurahContent(surahId, `${surahId}:1`);
  };

  // Load default surah if none selected
  useEffect(() => {
    if (!quranData.selectedSurah) {
      quranData.loadSurahContent('1', '1:1');
    }
  }, [quranData.selectedSurah, quranData.loadSurahContent]);

  // Track loading state for smooth transitions
  const [isLoading, setIsLoading] = useState(false);
  const [previousSurahContent, setPreviousSurahContent] = useState(null);

  // Handle surah content changes with loading states
  useEffect(() => {
    if (quranData.surahContent) {
      setPreviousSurahContent(quranData.surahContent);
      setIsLoading(false);
    }
  }, [quranData.surahContent]);

  // Show loading state when surah changes but content isn't loaded yet
  const displayContent = quranData.surahContent || previousSurahContent;
  const showLoadingOverlay = isLoading || (!quranData.surahContent && quranData.selectedSurah);

  return (
    <>
      {/* Side Tab - Only show when player is not expanded */}
      {!isPlayerExpanded && (
        <div 
          className="fixed left-0 top-1/2 transform -translate-y-1/2 z-40 cursor-pointer"
          onClick={handleSideTabClick}
        >
          <div className="backdrop-blur-md bg-white/5 border border-white/10 rounded-r-xl px-2 py-4 shadow-lg transition-all duration-300 hover:bg-white/8">
            <div className="flex flex-col items-center gap-1.5">
              <Play className="w-4 h-4 text-white/80 drop-shadow-sm" />
              <div className="w-px h-4 bg-white/20"></div>
              <span className="text-white/70 text-[10px] font-light writing-mode-vertical transform -rotate-180 drop-shadow-sm tracking-wider">
                QURAN
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Player */}
      {isPlayerExpanded && (
        <div 
          className={`fixed inset-0 z-[60] flex flex-col transform transition-all duration-300 ${
            isClosing ? 'animate-slide-down' : 'animate-slide-up'
          }`}
          style={{
            paddingBottom: window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches
              ? 'calc(env(safe-area-inset-bottom) + 3.2rem)'
              : navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad')
                ? 'calc(env(safe-area-inset-bottom) + 50px)'
                : '4rem'
          }}
        >
          {/* Background with slideshow showing through */}
          <div className="absolute inset-0">
            {/* Background Images */}
            {backgroundImages && backgroundImages.length > 0 && (
              <>
                {/* Previous background */}
                <div
                  className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 opacity-100"
                  style={{
                    backgroundImage: `url("${backgroundImages[previousBackgroundIndex]}")`,
                  }}
                />
                {/* Current background */}
                <div
                  className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 opacity-100"
                  style={{
                    backgroundImage: `url("${backgroundImages[currentBackgroundIndex]}")`,
                  }}
                />
              </>
            )}
            
            {/* Glassmorphic overlay */}
            <div className="absolute inset-0 backdrop-blur-xl bg-black/40" />
            
            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 opacity-[0.02]" 
                 style={{
                   backgroundImage: 'url("/images/bgpattern.png")', 
                   backgroundSize: '400px',
                   filter: 'invert(1)', 
                   mixBlendMode: 'multiply'
                 }}>
            </div>
          </div>

          {/* Header section with close button */}
          <div className="relative pt-[calc(env(safe-area-inset-top)_+_1rem)] px-4 pb-4 flex items-center backdrop-blur-sm bg-white/5 border-b border-white/10">
            <button 
              onClick={handleClose}
              className="transition-transform duration-200 hover:scale-110 relative z-10 text-white/90"
            >
              <HiChevronDown className="h-6 w-6" />
            </button>
            <div className="absolute left-0 right-0 text-center z-[1]">
              <div className="text-sm font-medium text-white/90">Now Playing</div>
            </div>
          </div>
          
          {/* Two Column Layout */}
          <div className="flex-1 flex overflow-hidden animate-fade-in" ref={containerRef}>
            {/* Left Column - Player (2/3 width) */}
            <div className="w-2/3 flex flex-col p-4 overflow-hidden">
              {selectedSurah && (
                <div className="flex flex-col items-center h-full">
                  <div className="w-full flex flex-col items-center">
                    <div className="w-full flex justify-center -mt-8">
                      <img 
                        src={`/images/quran_chapters/Surah_${selectedSurah}_of_114.svg`}
                        alt={`Surah ${selectedSurah}`}
                        className={`w-[55%] h-auto filter brightness-0 invert transition-opacity duration-300 ${
                          showLoadingOverlay ? 'opacity-50' : 'opacity-90'
                        }`}
                      />
                    </div>
                    
                    <div className="text-center -mt-6">
                      <div className="text-xs leading-tight text-white/60">
                        Surah {selectedSurah}
                      </div>
                      <div className={`text-sm font-medium leading-tight transition-opacity duration-300 ${
                        showLoadingOverlay ? 'text-white/50' : 'text-white/90'
                      }`}>
                        {quranData.surahs?.find(s => s.id.toString() === selectedSurah)?.name_simple}
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 w-full flex flex-col mt-12 overflow-hidden relative">
                    {/* Loading Overlay */}
                    {showLoadingOverlay && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm z-10 animate-fade-in">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-8 h-8 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin"></div>
                          <p className="text-white/70 text-sm">Loading surah...</p>
                        </div>
                      </div>
                    )}

                    {/* Content Area */}
                    {displayContent && displayContent[currentVerse] && (
                      <div 
                        ref={contentRef}
                        className={`w-full h-full space-y-4 text-center overflow-y-auto scrollbar-none transition-opacity duration-300 ${
                          showLoadingOverlay ? 'opacity-30' : 'opacity-100'
                        }`}
                        style={{
                          maskImage: 'linear-gradient(to bottom, transparent, white 10%, white 90%, transparent)',
                          WebkitMaskImage: 'linear-gradient(to bottom, transparent, white 10%, white 90%, transparent)'
                        }}
                        onScroll={handleScroll}
                      >
                        <div className="px-4 py-8">
                          <p 
                            key={`arabic-${currentVerse}-${selectedSurah}`} 
                            className="text-2xl leading-loose font-arabic transition-opacity duration-500 ease-in-out text-white/95"
                            dir="rtl"
                          >
                            {displayContent[currentVerse].text_uthmani}
                          </p>

                          <div 
                            key={`translation-${currentVerse}-${selectedSurah}`}
                            className="text-base px-4 mt-4 transition-opacity duration-500 ease-in-out text-white/80"
                          >
                            {displayContent[currentVerse].translations?.find(t => 
                              t.resource_id.toString() === (settings.selectedTranslation || '131')
                            )?.text.split(/<sup.*?<\/sup>/).map((part, i, arr) => (
                              <React.Fragment key={i}>
                                {part}
                                {i < arr.length - 1 && (() => {
                                  const footnoteMatch = displayContent[currentVerse].translations
                                    ?.find(t => t.resource_id.toString() === (settings.selectedTranslation || '131'))
                                    ?.text
                                    .split(/<sup.*?<\/sup>/)[i + 1]
                                    ?.match(/<sup foot_note=(\d+)>(\d+)<\/sup>/);
                                  if (footnoteMatch) {
                                    const footnoteId = footnoteMatch[1];
                                    return (
                                      <sup
                                        className="cursor-pointer ml-[1px] text-emerald-400"
                                      >
                                        [{footnoteMatch[2]}]
                                      </sup>
                                    );
                                  }
                                  return null;
                                })()}
                              </React.Fragment>
                            ))}
                          </div>
                          <div className="h-48" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Player controls */}
                  <div 
                    className={`w-full backdrop-blur-xl border-t border-white/10 bg-black/20 mt-4 transition-opacity duration-300 ${
                      showLoadingOverlay ? 'opacity-50' : 'opacity-100'
                    }`}
                    style={{
                      height: '120px'
                    }}
                  >
                    <div className="flex items-center justify-center space-x-12 h-full">
                      <button 
                        className={`w-12 h-12 flex items-center justify-center transition-colors text-white/70 hover:text-emerald-400 ${
                          currentVerse <= 0 || showLoadingOverlay ? 'opacity-50' : ''
                        }`}
                        onClick={handlePrevVerse}
                        disabled={currentVerse <= 0 || showLoadingOverlay}
                        style={{
                          WebkitTapHighlightColor: 'transparent',
                          touchAction: 'manipulation'
                        }}
                      >
                        <SkipBack className="w-6 h-6" />
                      </button>

                      <button 
                        className={`w-16 h-16 flex items-center justify-center rounded-full backdrop-blur-sm border border-emerald-400/20 transition-all transform hover:scale-105 bg-emerald-950/30 text-emerald-400 hover:bg-emerald-900/40 ${
                          showLoadingOverlay ? 'opacity-50' : ''
                        }`}
                        style={{
                          boxShadow: '0 0 30px rgba(52, 211, 153, 0.2)',
                          WebkitTapHighlightColor: 'transparent',
                          touchAction: 'manipulation'
                        }}
                        onClick={handlePlayClick}
                        disabled={showLoadingOverlay}
                      >
                        {quranAudio.isPlaying ? (
                          <Pause className="w-7 h-7" />
                        ) : (
                          <Play className="w-7 h-7 ml-1" />
                        )}
                      </button>

                      <button 
                        className={`w-12 h-12 flex items-center justify-center transition-colors text-white/70 hover:text-emerald-400 ${
                          !displayContent || currentVerse >= displayContent.length - 1 || showLoadingOverlay ? 'opacity-50' : ''
                        }`}
                        onClick={handleNextVerse}
                        disabled={!displayContent || currentVerse >= displayContent.length - 1 || showLoadingOverlay}
                        style={{
                          WebkitTapHighlightColor: 'transparent',
                          touchAction: 'manipulation'
                        }}
                      >
                        <SkipForward className="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Surah List (1/3 width) */}
            <div className="w-1/3 border-l border-white/10 backdrop-blur-sm bg-white/5">
              <div className="h-full flex flex-col">
                {/* Header */}
                <div className="p-4 border-b border-white/10">
                  <h3 className="text-lg font-medium text-white/90 mb-1">Select Surah</h3>
                  <p className="text-xs text-white/60">Choose a chapter to recite</p>
                </div>

                {/* Surah List */}
                <div className="flex-1 overflow-y-auto scrollbar-none">
                  {quranData.surahs?.map((surah) => (
                    <button
                      key={surah.id}
                      onClick={() => handleSurahSelect(surah.id.toString())}
                      className={`w-full p-4 text-left border-b border-white/5 transition-colors hover:bg-white/10 ${
                        selectedSurah === surah.id.toString() 
                          ? 'bg-emerald-500/20 border-l-4 border-l-emerald-400' 
                          : ''
                      }`}
                      style={{
                        WebkitTapHighlightColor: 'transparent',
                        touchAction: 'manipulation'
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                              selectedSurah === surah.id.toString()
                                ? 'bg-emerald-400 text-black'
                                : 'bg-white/10 text-white/70'
                            }`}>
                              {surah.id}
                            </div>
                            <div>
                              <div className={`text-sm font-medium ${
                                selectedSurah === surah.id.toString()
                                  ? 'text-emerald-400'
                                  : 'text-white/90'
                              }`}>
                                {surah.name_simple}
                              </div>
                              <div className="text-xs text-white/60">
                                {surah.verses_count} verses • {surah.revelation_place}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-lg font-arabic ${
                            selectedSurah === surah.id.toString()
                              ? 'text-emerald-400'
                              : 'text-white/70'
                          }`} dir="rtl">
                            {surah.name_arabic}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SimpleQuranPlayerCard; 